import { useEffect } from 'react';
import {
	dataLayerPush,
	type EcommerceClearEvent,
	type PageDataEvent,
	type ViewItemEvent,
} from '@ds-mgnl/marketing';
import type { ProductResponse } from '../services/productApi';

/**
 * Pushes the ecommerce-clear, page_data, and view_item dataLayer events for a
 * product detail-style page. Shared by ProductDetailPage and FabricPage.
 */
export function useProductPageViewAnalytics(
	product: ProductResponse | null | undefined,
	pageType: string = 'product_detail',
): void {
	useEffect(() => {
		if (!product?.id) {
			return;
		}

		// pimHierarchy (e.g. "pim:///Healthcare/Furnishings/Seating") is parsed as a
		// URL so its pathname segments become the product_category hierarchy.
		const productCategoryHierarchy = (
			URL.parse(product.pimHierarchy ?? '')?.pathname ?? ''
		)
			.split('/')
			.map((x) => decodeURIComponent(x))
			.filter((x) => x);

		const emptyCommerceEvent: EcommerceClearEvent = {
			ecommerce: null,
		};
		dataLayerPush(emptyCommerceEvent);

		const pageDataEvent: PageDataEvent = {
			event: 'page_data',
			page_type: pageType,
			product_id: product.id ?? '',
			product_name: product.name ?? '',
			product_category: productCategoryHierarchy.join(' | '),
			product_brand: product.manufacturerName ?? '',
			product_price: product.price ?? 0,
		};
		dataLayerPush(pageDataEvent);

		const [item_category, item_category2, item_category3, item_category4] =
			productCategoryHierarchy;
		const viewItemEvent: ViewItemEvent = {
			event: 'view_item',
			ecommerce: {
				currency: 'USD',
				value: product.price ?? 0,
				items: [
					{
						item_id: product.id ?? '',
						item_name: product.name ?? '',
						item_brand: product.manufacturerName ?? '',
						item_category,
						item_category2,
						item_category3,
						item_category4,
						price: product.price ?? 0,
						quantity: 1,
					},
				],
			},
		};
		dataLayerPush(viewItemEvent);
	}, [
		product?.id,
		product?.manufacturerName,
		product?.name,
		product?.pimHierarchy,
		product?.price,
		pageType,
	]);
}
